// apps/web/app/microgrid/lit/components/Hero.tsx
'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { seekTo, VIDEO_START_TIME } from '../../shared/video';
import { track, routePing } from '../components/ga';
import './styles/Hero.css';

const Hero: React.FC = () => {
  const timersRef = useRef<number[]>([]);
  const isAnimationComplete = useRef(false);
  const videoEndHandlerRef = useRef<((this: HTMLVideoElement, ev: Event) => any) | null>(null);

  useEffect(() => {
    // page_view (child -> parent)
    routePing();

    const body = document.body;
    body.classList.remove('loading', 'loaded');
    body.classList.add('loading');

    const logo = document.querySelector('nav .logo') as HTMLElement | null;
    const titleEl = document.getElementById('hero-title') as HTMLHeadingElement | null;
    const video = document.getElementById('hero-video') as HTMLVideoElement | null;
    if (!titleEl || !logo || !video) return;

    const startVideo = async () => {
      try {
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('autoplay', '');
        video.muted = true;
        video.defaultMuted = true;
        (video as any).playsInline = true;
        (video as any).webkitPlaysInline = true;
        video.autoplay = true;
        video.volume = 0;

        if (video.readyState < 1) {
          await new Promise<void>((res) => video.addEventListener('loadedmetadata', () => res(), { once: true }));
        }

        await video.play().catch(() => {});
        video.pause();

        if (!isFinite(video.duration) || Number.isNaN(video.duration)) {
          video.load();
          await new Promise<void>((res) => video.addEventListener('loadedmetadata', () => res(), { once: true }));
        }

        await seekTo(video, VIDEO_START_TIME);
        await video.play();
        video.classList.add('ready');
        track('video_start', { video_title: 'AL Hero', video_url: '/hero.mp4', location: 'hero' });

        videoEndHandlerRef.current = function () {
          this.currentTime = 3;
          this.play();
          track('video_loop', { video_title: 'AL Hero' });
        };
        video.addEventListener('ended', videoEndHandlerRef.current);
      } catch {}
    };

    const skipAnimation = () => {
      if (isAnimationComplete.current) return;
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
      body.classList.remove('loading');
      body.classList.add('loaded');
      logo?.classList.add('lit');
      titleEl.classList.add('hydrated');
      titleEl.querySelectorAll<HTMLSpanElement>('.word-span').forEach((s) => s.classList.add('lit'));
      startVideo();
      isAnimationComplete.current = true;
      track('hero_intro_skipped', {});
    };

    const onSkipEvent = () => skipAnimation();
    window.addEventListener('al:skip-anim', onSkipEvent);

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      skipAnimation();
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !isAnimationComplete.current) startVideo();
    };
    const onFirstTouch = () => {
      if (!isAnimationComplete.current) startVideo();
      document.removeEventListener('touchend', onFirstTouch);
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('touchend', onFirstTouch, { once: true });

    // split title & animate
    const text = titleEl.textContent || '';
    const words = text.split(' ');
    titleEl.innerHTML = '';
    const spans: HTMLSpanElement[] = [];
    words.forEach((word, index) => {
      const span = document.createElement('span');
      span.className = 'word-span';
      span.textContent = index < words.length - 1 ? `${word}\u00A0` : word;
      titleEl.appendChild(span);
      spans.push(span);
    });
    // force style calc
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    titleEl.offsetWidth;
    titleEl.classList.add('hydrated');

    const runIntro = () => {
      const base = words.length * 300;
      const LOGO_OFFSET = 500;
      const AFTER_LOGO_TO_LOADED = 900;
      const AFTER_LOADED_TO_VIDEO = 200;

      spans.forEach((span, idx) => {
        timersRef.current.push(window.setTimeout(() => span.classList.add('lit'), idx * 300));
      });
      timersRef.current.push(
        window.setTimeout(() => {
          logo?.classList.add('lit');
          track('hero_logo_reveal', {});
        }, base + LOGO_OFFSET)
      );
      timersRef.current.push(
        window.setTimeout(() => {
          body.classList.remove('loading');
          body.classList.add('loaded');
          isAnimationComplete.current = true;
          track('hero_intro_complete', {});
        }, base + LOGO_OFFSET + AFTER_LOGO_TO_LOADED)
      );
      timersRef.current.push(window.setTimeout(() => startVideo(), base + LOGO_OFFSET + AFTER_LOGO_TO_LOADED + AFTER_LOADED_TO_VIDEO));
    };

    if (location.hash) {
      skipAnimation();
    } else {
      runIntro();
    }

    const onClickFastForward = () => skipAnimation();
    document.addEventListener('click', onClickFastForward);

    const onHashChange = () => {
      routePing(); // record route/hash change while in iframe
      skipAnimation();
    };
    window.addEventListener('hashchange', onHashChange);

    return () => {
      timersRef.current.forEach(clearTimeout);
      document.removeEventListener('click', onClickFastForward);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('touchend', onFirstTouch);
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('al:skip-anim', onSkipEvent);
      if (video && videoEndHandlerRef.current) video.removeEventListener('ended', videoEndHandlerRef.current);
      body.classList.remove('loading', 'loaded');
      document.querySelector('nav .logo')?.classList.remove('lit');
    };
  }, []);

  return (
    <section className="hero">
      <video
        id="hero-video"
        className="hero-video"
        muted
        playsInline
        autoPlay
        preload="auto"
        aria-hidden="true"
        tabIndex={-1}
        controls={false}
        disablePictureInPicture
        disableRemotePlayback
        controlsList="nodownload noremoteplayback noplaybackrate nofullscreen"
        poster="/hero-poster.jpg"
      >
        <source src="/hero.mp4" type="video/mp4" />
      </video>

      <div className="hero-text">
        <h1 id="hero-title">Transform Your Night. One Light At A Time.</h1>
        <p>Experience intelligent, beautiful, and fully customizable outdoor lighting that brings your home to life after dark.</p>
        <Link
          href="https://alwayslittx.com/vision-engine"
          prefetch={false}
          target="_top"
          className="cta-button"
          onClick={() => track('select_content', { content_type: 'link', item_id: 'hero_cta', link_url: 'https://alwayslittx.com/vision-engine', location: 'hero' })}
        >
          See The Magic
        </Link>
      </div>
    </section>
  );
};

export default Hero;
